"use client"

import { useState } from "react"
import { db, auth } from "@/lib/firebase"
import { collection, getDocs, deleteDoc, doc, writeBatch } from "firebase/firestore"
import { ShieldAlert, Trash2, Loader2, Database, AlertTriangle, RefreshCw } from "lucide-react"
import { toast } from "sonner"

export default function MaintenanceManager() {
  const [isWipingUsers, setIsWipingUsers] = useState(false)
  const [isWipingEnrollments, setIsWipingEnrollments] = useState(false)
  const [isResettingSystem, setIsResettingSystem] = useState(false)

  const wipeCollection = async (collectionName: string, setter: (val: boolean) => void) => {
    const confirmed = window.confirm(`CRITICAL ACTION: Are you sure you want to permanently DELETE ALL records in the "${collectionName}" collection? This cannot be undone.`)
    if (!confirmed) return

    setter(true)
    try {
      const snapshot = await getDocs(collection(db, collectionName))
      const batch = writeBatch(db)
      
      snapshot.docs.forEach((document) => {
        batch.delete(doc(db, collectionName, document.id))
      })

      await batch.commit()
      toast.success(`System Cleanse Complete: "${collectionName}" wiped.`)
    } catch (error) {
      console.error(error)
      toast.error(`Cleansing failed for "${collectionName}". Check Security Rules.`)
    } finally {
      setter(false)
    }
  }

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="flex flex-col gap-2">
         <h2 className="text-3xl font-serif font-bold text-slate-950">System Maintenance</h2>
         <p className="text-slate-500 max-w-2xl">Perform high-level administrative tasks to manage the database infrastructure. These actions are destructive and permanent.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
         
         {/* Wipe Users Card */}
         <div className="bg-white border border-slate-200 p-8 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
            <div className="space-y-4 mb-8">
               <div className="w-12 h-12 bg-rose-50 flex items-center justify-center text-rose-500">
                  <ShieldAlert size={24} />
               </div>
               <div>
                  <h3 className="text-xl font-serif font-bold text-slate-900">Clear User Directory</h3>
                  <p className="text-sm text-slate-500 mt-2 leading-relaxed">Permanently removes all student and staff records from Firestore. Authentication accounts will remain, but profiles will be reset.</p>
               </div>
            </div>
            <button 
              onClick={() => wipeCollection("users", setIsWipingUsers)}
              disabled={isWipingUsers}
              className="w-full bg-white border border-rose-200 text-rose-600 py-4 text-[10px] font-bold uppercase tracking-widest hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
               {isWipingUsers ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 size={14} /> Wipe User Database</>}
            </button>
         </div>

         {/* Wipe Enrollments Card */}
         <div className="bg-white border border-slate-200 p-8 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
            <div className="space-y-4 mb-8">
               <div className="w-12 h-12 bg-amber-50 flex items-center justify-center text-amber-600">
                  <Database size={24} />
               </div>
               <div>
                  <h3 className="text-xl font-serif font-bold text-slate-900">Purge Enrollments</h3>
                  <p className="text-sm text-slate-500 mt-2 leading-relaxed">Clears all course purchase and enrollment history. Students will lose access to all previously bought courses immediately.</p>
               </div>
            </div>
            <button 
              onClick={() => wipeCollection("enrollments", setIsWipingEnrollments)}
              disabled={isWipingEnrollments}
              className="w-full bg-white border border-amber-200 text-amber-600 py-4 text-[10px] font-bold uppercase tracking-widest hover:bg-amber-600 hover:text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
               {isWipingEnrollments ? <Loader2 className="w-4 h-4 animate-spin" /> : <><AlertTriangle size={14} /> Purge All Enrollments</>}
            </button>
         </div>

         {/* Wipe Global Stats Card */}
         <div className="bg-white border border-slate-200 p-8 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow col-span-1 md:col-span-2">
            <div className="flex flex-col md:flex-row items-center gap-8">
               <div className="w-20 h-20 bg-slate-950 flex items-center justify-center text-[#b48c36] shrink-0 shadow-xl">
                  <RefreshCw size={32} />
               </div>
               <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl font-serif font-bold text-slate-900">Total System Re-Initialization</h3>
                  <p className="text-sm text-slate-500 mt-2 leading-relaxed max-w-xl">Reset the entire platform to factory settings. This clears users, enrollments, logs, and interaction data. Courses and content are preserved.</p>
               </div>
               <button 
                 onClick={async () => {
                    const confirmed = window.confirm("ULTIMATE WARNING: This will wipe EVERYTHING except content (Courses/Articles). Proceed?")
                    if (!confirmed) return
                    setIsResettingSystem(true)
                    try {
                      await wipeCollection("users", () => {})
                      await wipeCollection("enrollments", () => {})
                      await wipeCollection("orders", () => {}) // If exists
                      toast.success("System Identity Reset successfully.")
                    } catch (e) {
                      toast.error("Global reset failed.")
                    } finally {
                      setIsResettingSystem(false)
                    }
                 }}
                 disabled={isResettingSystem}
                 className="bg-slate-950 text-white px-10 py-5 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-[#b48c36] transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
               >
                  {isResettingSystem ? <Loader2 className="w-4 h-4 animate-spin" /> : "Initiate Full Cleanse"}
               </button>
            </div>
         </div>

      </div>

      <div className="bg-slate-50 p-6 border-l-4 border-[#b48c36] text-slate-600 text-sm italic font-serif">
         "Maintenance mode ensures the platform remains efficient and secure by purging legacy testing data. Proceed with caution."
      </div>
    </div>
  )
}
